import { useCallback, useMemo, useState } from 'react';
import { REVENUE_MAPPED_TYPES, ALLOW_CUSTOM_REVENUE_MAPPED_TYPES } from '../../constants/revenueMappedTypes';

// Shared helpers for the Revenue map page (RevenueTab / RoomStatsTab).

// Level-1 forecast segments (hotelOccupancyForecast.segmentGroup vocabulary).
// Classifying a revenue type here lets segment report views count its
// actuals toward that segment (blended with the segment forecast).
export const SEGMENT_GROUP_OPTIONS = [
  { value: 'group', label: 'Group' },
  { value: 'transient', label: 'Transient' },
  { value: 'contract', label: 'Contract' },
  { value: 'other', label: 'Other' },
];

export const OPERATION_OPTIONS = [
  { value: 'add', label: 'Add' },
  { value: 'subtract', label: 'Subtract' },
  { value: 'replace', label: 'Replace' },
];
export const ACCOUNT_OPERATION_OPTIONS = OPERATION_OPTIONS.filter((o) => o.value !== 'replace');

// Room-stat mapped types that can also be sourced from statistical COA accounts.
export const COA_SOURCED_STAT_TYPES = new Set(['Available Rooms', 'Occupied Rooms']);

export const isDni = (mappedType) => String(mappedType || '').trim().toUpperCase() === 'DNI';
export const isBlank = (v) => v == null || String(v).trim() === '';

/** COA options for InlineSelect/InlineMultiSelect: label "40000 - Room Revenue", hint = department. */
export function buildCoaOptions(chartAccounts) {
  return (chartAccounts || []).map((a) => ({
    value: a.id,
    label: `${a.accountNumber ?? ''} - ${a.accountName ?? ''}`.trim(),
    hint: a.department || undefined,
  }));
}

export function buildCoaMap(chartAccounts) {
  const m = new Map();
  (chartAccounts || []).forEach((a) => m.set(a.id, a));
  return m;
}

export const coaLabel = (coaMap, id) => {
  const a = coaMap.get(id);
  return a ? `${a.accountNumber ?? ''} - ${a.accountName ?? ''}`.trim() : (id != null ? `Account ${id}` : '');
};

/**
 * mappedType options for the Type select. The canonical vocabulary plus, when the
 * row currently holds something outside it, that value as a "custom" option so
 * legacy per-property labels keep rendering instead of vanishing.
 */
export function mappedTypeOptions(currentValue) {
  const opts = REVENUE_MAPPED_TYPES.map((v) => ({
    value: v,
    label: v,
    hint: v === 'DNI' ? 'Do not include' : (v === 'Room Revenue' ? 'Rooms · occupancy math' : undefined),
  }));
  if (!isBlank(currentValue) && !REVENUE_MAPPED_TYPES.includes(currentValue)) {
    opts.push({ value: currentValue, label: currentValue, hint: 'custom' });
  }
  return opts;
}
export const allowCustomMappedType = ALLOW_CUSTOM_REVENUE_MAPPED_TYPES;

/**
 * Where does a revenue type land?
 *   - The TYPE BUCKET (mappedType) is what the dashboards and revenue reports
 *     read — a blank bucket is the only true "unmapped" state.
 *   - 'DNI' is a decision (do not include), not a gap.
 *   - The GL account link is secondary: it adds the P&L/GL line; many orgs map
 *     revenue to GL through budget accounts instead, so its absence is
 *     informational (amber dot), never counted as unmapped.
 * Returns { state: 'complete'|'partial'|'none', title, unmapped, reason }.
 */
export function describeRevenueType(type, coaMap) {
  if (!type) return { state: 'none', title: '', unmapped: false };
  if (type.isDeleted) return { state: 'none', title: 'Deleted — ignored everywhere', unmapped: false, reason: 'deleted' };
  const acct = type.chartOfAccountId != null ? coaMap.get(type.chartOfAccountId) : null;
  const noCoa = type.chartOfAccountId == null;
  const noType = isBlank(type.mappedType);
  const dni = isDni(type.mappedType);
  if (noType) {
    return {
      state: 'partial',
      title: acct
        ? `No type — GL account ${acct.accountNumber} is set but the dashboards skip untyped revenue`
        : 'No type — excluded from dashboards and revenue reports',
      unmapped: true,
      reason: 'noType',
    };
  }
  if (dni) return { state: 'none', title: `DNI — do not include (excluded by decision)${acct ? `; GL account ${acct.accountNumber}` : ''}`, unmapped: false, reason: 'dni' };
  if (noCoa) return { state: 'complete', title: `${type.mappedType} — reaches the dashboards; no GL account linked (P&L via budget accounts)`, unmapped: false, reason: 'noCoa' };
  const dept = acct?.department ? ` · ${acct.department}` : '';
  return {
    state: 'complete',
    title: `${type.mappedType} · GL ${acct ? acct.accountNumber : type.chartOfAccountId}${dept}`,
    unmapped: false,
    reason: null,
  };
}

/** Generic sort state hook shared by both tabs. */
export function useSortConfig(defaultKey = 'name') {
  const [sortConfig, setSortConfig] = useState({ key: defaultKey, direction: 'ascending' });
  const requestSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  }, []);
  return [sortConfig, requestSort];
}

export function sortRows(rows, sortConfig, valueFor) {
  if (!sortConfig?.key) return rows;
  const dir = sortConfig.direction === 'ascending' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = valueFor(a, sortConfig.key);
    const bv = valueFor(b, sortConfig.key);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
  });
}

/** Selection-set hook (row checkboxes). */
export function useSelection() {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const toggleMany = useCallback((ids) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const all = ids.length > 0 && ids.every((id) => next.has(id));
      ids.forEach((id) => (all ? next.delete(id) : next.add(id)));
      return next;
    });
  }, []);
  const clear = useCallback(() => setSelectedIds(new Set()), []);
  return { selectedIds, toggleOne, toggleMany, clear, setSelectedIds };
}

export function useSelectionState(visibleIds, selectedIds) {
  return useMemo(() => {
    const all = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    const some = !all && visibleIds.some((id) => selectedIds.has(id));
    return { all, some };
  }, [visibleIds, selectedIds]);
}

/** Bulk-bar secondary button (dark bar → light ghost). */
export const bulkGhostBtnStyle = (disabled) => ({
  padding: '5px 12px', border: '1px solid rgba(255,255,255,.14)', borderRadius: 6,
  fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,.85)', background: 'transparent',
  cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-sans)', flexShrink: 0,
});

/** Group by a key → Map(key → items[]). */
export function groupBy(items, keyFn) {
  const m = new Map();
  (items || []).forEach((it) => {
    const k = keyFn(it);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(it);
  });
  return m;
}
